// MEO landmark ads (Phase 1, Step 4-3, 2026-05-01):
// landmarks 管理画面 — List + Delete action のみ (MVP)。
// Create / Edit dialog は次 PR、Mapbox 連動は将来 PR。
//
// Spec: design/meo-landmark-ads-spec.md §UX/UI §管理者 §ランドマーク管理画面
//
// auth: ADMIN (全 landmark) / BUSINESS (自分が sponsor の landmark のみ)。
// CMS 側はログイン user の role_id に応じて backend 自動フィルタ。

import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { getCurrentRoleId } from '@/utils/routes';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import LandmarkFormSheet from './components/LandmarkFormSheet';

// 地図の公開範囲 (2026-11-04 §50)。期間の外なら「期間外」を添える。非表示 (既定) は何も出さない。
const MapVisibilityBadge = ({ landmark }: { landmark: any }) => {
    const v = landmark.map_visibility;
    if (v !== 'public' && v !== 'limited') return null;
    const now = Date.now();
    const from = landmark.map_visible_from ? new Date(landmark.map_visible_from).getTime() : null;
    const until = landmark.map_visible_until ? new Date(landmark.map_visible_until).getTime() : null;
    const outOfPeriod = (from != null && now < from) || (until != null && now > until);
    const label = v === 'public' ? '公開' : '限定公開';
    const color = outOfPeriod ?
        'bg-gray-100 text-gray-500' :
        (v === 'public' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800');
    return (
        <span className={`ml-2 inline-block rounded px-1.5 py-0.5 text-xs font-normal ${color}`}>
            {label}
            {outOfPeriod && ' (期間外)'}
        </span>
    );
};

// landmarks.category: spec §UX §管理者 §ランドマーク管理画面 で列挙された 5 種。
const CATEGORY_LABEL: Record<string, string> = {
    currency_exchange: '両替機',
    shrine: '神社',
    tourist: '観光地',
    shop: '店舗',
    other: 'その他'
};

const formatCategory = (cat?: string | null): string => {
    if (!cat) return '-';
    return CATEGORY_LABEL[cat] ?? cat;
};

export default observer(function LandmarkManagementPage() {
    const { t } = useTranslation();
    const {
        landmarkStore: { landmarks, total, list: fetchList, remove: removeLandmark },
        userStore: { businessUsers, loadBusinessUsers },
        modalStore: { showAlertModal }
    } = useStore();

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);

    useEffect(() => {
        fetchList();
        // スポンサー列の username 表示用 (2026-07-07 pitfall batch)。
        // GET /system/user は ADMIN 専用 + 403 は強制 logout のため ADMIN の時のみ。
        if (getCurrentRoleId() === 0) {
            flowResult(loadBusinessUsers());
        }
    }, []);

    // sponsor_user_id → username の解決 map (ADMIN のみ、BUSINESS は空 map で raw ID 表示に fallback)。
    const businessNameById = new Map(businessUsers.map(u => [Number(u.id), u.username]));

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (lm: any) => {
        setSheetMode('edit');
        setEditTarget(lm);
        setSheetOpen(true);
    };

    const handleDelete = (id: number, name: string) => {
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${name}」を削除しますか? (paranoid 削除のため復元可能)`,
            onSave: async () => {
                const res = await flowResult(removeLandmark(id));
                if (res?.ok) {
                    toastify('success', t('messages.delete_success') || '削除しました');
                    fetchList();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>
                        {t('words_title.landmark_management') || 'ランドマーク管理'}
                    </h2>
                    <p className='text-sm text-gray-500 mt-1'>
                        {t('sentences.landmark_total') || `登録件数: ${total}`}
                    </p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    {t('button.add_new') || '新規追加'}
                </Button>
            </div>

            {landmarks.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>
                        {t('messages.no_data') || '登録されているランドマークがありません'}
                    </div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    {/* No. = 表示連番、ID = 実 landmark.id の 2 列方針 (2026-07-08 pitfall batch #7、
                                        item / user 一覧と統一)。 */}
                                    <th className='py-2 px-3 w-[50px]'>No.</th>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>名称</th>
                                    <th className='py-2 px-3'>カテゴリ</th>
                                    <th className='py-2 px-3 text-right'>緯度</th>
                                    <th className='py-2 px-3 text-right'>経度</th>
                                    <th className='py-2 px-3 text-right'>半径(m)</th>
                                    <th className='py-2 px-3'>スポンサー事業者</th>
                                    <th className='py-2 px-3 w-[80px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {landmarks.map((lm, i) => (
                                    <tr key={lm.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-400'>
                                            {i + 1}
                                        </td>
                                        <td className='py-2 px-3 font-mono text-gray-500'>
                                            {lm.id}
                                        </td>
                                        <td className='py-2 px-3 font-medium'>
                                            {lm.name}
                                            <MapVisibilityBadge landmark={lm} />
                                        </td>
                                        <td className='py-2 px-3'>
                                            {formatCategory(lm.category)}
                                        </td>
                                        <td className='py-2 px-3 text-right font-mono'>
                                            {Number(lm.latitude).toFixed(6)}
                                        </td>
                                        <td className='py-2 px-3 text-right font-mono'>
                                            {Number(lm.longitude).toFixed(6)}
                                        </td>
                                        <td className='py-2 px-3 text-right'>
                                            {lm.radius_m ?? '-'}
                                        </td>
                                        <td className='py-2 px-3 text-gray-500'>
                                            {lm.sponsor_user_id == null ?
                                                '個人' :
                                                (businessNameById.get(Number(lm.sponsor_user_id)) ?
                                                    `${businessNameById.get(Number(lm.sponsor_user_id))} (ID: ${lm.sponsor_user_id})` :
                                                    lm.sponsor_user_id)}
                                        </td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleOpenEdit(lm)}
                                                >
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(lm.id, lm.name)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <LandmarkFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                landmark={editTarget}
                onSuccess={fetchList}
            />
        </div>
    );
});
